import {type PropsWithChildren, useCallback, useEffect, useId, useRef, useState} from 'react'
import {testIdProps} from '@github-ui/test-id-props'
import {VisuallyHidden} from '@github-ui/visually-hidden'
import {Box, Button, CounterLabel, type ButtonProps, type SxProp} from '@primer/react'
import {clsx} from 'clsx'

import {useListViewId} from '../ListView/IdContext'
import {ChevronDownIcon, ChevronRightIcon} from '@primer/octicons-react'
import {onListGroupEnter, onListGroupArrowRight, onListGroupArrowLeft} from './utils'
import {useListViewSelection} from '../ListView/SelectionContext'

export type ListGroupProps = PropsWithChildren<{
  /**
   * A title provides the ListGroup with a descriptive name that communicates what kind of content it
   * contains. Will be used as the text content of the group toggle button.
   */
  title: string
  /**
   * Controls whether or not the group is rendered with contents expanded (default, `true`)
   * or collapsed (`false`).
   */
  expanded?: boolean
  /**
   * The numeric value that is inserted into the toggle button's Button.Counter trailing content.
   * If omitted, the Counter is not rendered.
   */
  count?: number
}>

const liSx: SxProp['sx'] = {
  listStyle: 'none',
  margin: 0,
  padding: 0,
  outline: 'none',

  '&.listviewgroup > .listviewgroup-title': {
    borderTop: '1px solid',
    borderColor: 'border.muted',
    borderBottom: '0',
  },
  '&.listviewgroup-expanded > .listviewgroup-title': {
    borderBottom: '1px solid',
    borderColor: 'border.muted',

    '& > .listviewgroup-title-button:not(:hover)': {
      backgroundColor: 'transparent',
    },
  },
  ':first-child > .listviewgroup-title': {
    borderTop: '0',
    borderTopLeftRadius: 2,
    borderTopRightRadius: 2,
  },

  ':last-child:not(.listviewgroup-expanded) > .listviewgroup-title': {
    borderBottomLeftRadius: 2,
    borderBottomRightRadius: 2,
  },

  '&:focus-visible > div': {
    borderRadius: 0,
    outline: '2px solid var(--focus-outlineColor, var(--color-accent-fg))',
    outlineOffset: '-2px',
    boxShadow: 'none',
  },
}

const titleSx: SxProp['sx'] = {
  p: 2,
  backgroundColor: 'canvas.subtle',
  display: 'flex',
  gap: undefined,
  justifyContent: 'flex-start',
  alignItems: 'center',
}

const toggleButtonSx: ButtonProps['sx'] = {
  color: 'fg.default',
  ':has([data-component="ButtonCounter"])': {color: 'fg.default'},
}

const ulSx: SxProp['sx'] = {
  listStyle: 'none',
  m: 0,
  p: 0,
}

export function ListGroup({title, count, children, expanded = true}: ListGroupProps) {
  const {idPrefix} = useListViewId()
  const {singularUnits, pluralUnits} = useListViewSelection()
  const uniqueIdSuffix = useId()
  const groupTitleId = `${idPrefix}-listview-group-title-${uniqueIdSuffix}`
  const groupItemsId = `${idPrefix}-listview-group-items-${uniqueIdSuffix}`
  const [isExpanded, setIsExpanded] = useState(expanded)
  const liRef = useRef<HTMLLIElement>(null)

  useEffect(() => setIsExpanded(expanded), [expanded])

  const toggleExpanded = useCallback(
    (event?: React.MouseEvent | React.KeyboardEvent) => {
      setIsExpanded(!isExpanded)
      event?.stopPropagation()
    },
    [isExpanded],
  )

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLElement>) => {
      // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
      switch (event.key) {
        case 'Enter':
        case ' ': // Space
          onListGroupEnter(event, liRef.current, toggleExpanded)
          break
        case 'ArrowRight':
          onListGroupArrowRight(event, liRef.current, () => setIsExpanded(true))
          break
        case 'ArrowLeft':
          onListGroupArrowLeft(event, liRef.current, () => setIsExpanded(false))
          break
      }
    },
    [toggleExpanded],
  )

  return (
    <Box
      as="li"
      role="listitem"
      className={clsx(isExpanded && 'listviewgroup-expanded', 'listviewgroup')}
      sx={liSx}
      tabIndex={0}
      onKeyDown={handleKeyDown}
      ref={liRef}
      {...testIdProps('listview-group')}
    >
      <Box className="listviewgroup-title" sx={titleSx} {...testIdProps('listview-group-title')}>
        <Button
          leadingVisual={isExpanded ? ChevronDownIcon : ChevronRightIcon}
          variant="invisible"
          aria-controls={groupItemsId}
          onClick={event => toggleExpanded(event)}
          sx={toggleButtonSx}
          aria-expanded={isExpanded}
          className="listviewgroup-title-button"
          tabIndex={-1}
          id={groupTitleId}
        >
          {title}

          {count !== undefined && (
            <>
              <CounterLabel sx={{ml: 2}}>
                {count}
                <VisuallyHidden>{count === 1 ? singularUnits : pluralUnits}</VisuallyHidden>
              </CounterLabel>
            </>
          )}
        </Button>
      </Box>
      {isExpanded && (
        <Box as="ul" sx={ulSx} id={groupItemsId} aria-labelledby={groupTitleId} {...testIdProps('list-group-items')}>
          {children}
        </Box>
      )}
    </Box>
  )
}

try{ ListGroup.displayName ||= 'ListGroup' } catch {}